/**
 * ShareModal.jsx
 *
 * Two-step flow (logic unchanged from the original build):
 *   Step 1 — Wallet input with ETH address validation (regex: 0x + 40 hex chars)
 *   Step 2 — Video preview + auto-download + X / Twitter share intent
 *
 * The MediaRecorder WebM trick for duration:
 *   WebM files recorded by MediaRecorder have no duration in the header.
 *   Setting currentTime = 1e101 forces the browser to seek/scan and
 *   then we reset to 0. This makes the video scrubber work properly.
 *
 * Props:
 *   videoBlob {Blob|null}   Canvas recording from SpaceGame
 *   score     {number}      Final score, saved alongside the wallet
 *   onClose   {() => void}  Called when the modal should close
 */

import React, { useEffect, useRef, useState } from 'react';
import { useToast } from '../context/ToastContext';
import { saveWallet } from '../lib/wallets';
import GlowText from './GlowText';
import RoboticButton from './RoboticButton';

const X_HANDLE = 'HexaBots_';
const SITE_URL = 'https://hexabots.xyz/';

export default function ShareModal({ videoBlob, score = 0, onClose }) {
  const toast = useToast();
  const videoRef = useRef(null);

  const [step,     setStep]     = useState(1);
  const [wallet,   setWallet]   = useState('');
  const [videoUrl, setVideoUrl] = useState(null);
  const [saving,   setSaving]   = useState(false);
  const [needsTap, setNeedsTap] = useState(false);

  const ext = videoBlob?.type.includes('mp4') ? 'mp4' : 'webm';

  // The popup must not be dismissible until the wallet form is actually
  // submitted (step 2) — no backdrop click, no close button, no Escape.
  // Refreshing the page is the only other way out, by design.
  const canClose = step === 2;

  // Create an object URL for the video blob and revoke it on unmount
  useEffect(() => {
    if (!videoBlob) return;
    const url = URL.createObjectURL(videoBlob);
    setVideoUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [videoBlob]);

  // ── Step 1 — Validate wallet, save it to Supabase, then advance ────
  const next = async () => {
    const w = wallet.trim();
    if (!/^0x[a-fA-F0-9]{40}$/.test(w)) {
      toast.error('Invalid ETH wallet — must be 0x + 40 hex chars.');
      return;
    }
    if (saving) return;

    setSaving(true);
    try {
      const { alreadyExists } = await saveWallet(w, score);
      toast.success(
        alreadyExists ? 'Wallet already locked in ✅' : 'Wallet locked in ✅'
      );
    } catch (err) {
      console.error('Failed to save wallet to Supabase:', err);
      toast.error("Couldn't reach the whitelist server — grab your video below and we'll follow up.");
    } finally {
      setSaving(false);
      setStep(2);
    }
  };

  // ── Step 2 — Download video + open X share intent ─────────────────
  //
  // The wallet address is deliberately NOT in this post. It used to be,
  // and that was a real privacy problem: a public tweet permanently ties
  // the poster's X identity to their on-chain address, so anyone can see
  // their full balance and transaction history forever, and it hands
  // scammers a confirmed pair to target. The wallet still reaches us —
  // it was saved to the database in step 1 — so putting it in the tweet
  // achieved nothing except exposing the user.
  //
  // The site link goes in instead, so the post actually recruits players.
  const tweetText =
    `I scored ${score} in the @${X_HANDLE} pilot run 🤖\n\n` +
    `2,222 hex-headed robots minting on ARC chain.\n\n` +
    `Secure your spot too like me\n${SITE_URL}\n\n` +
    `#HEXABOTS #NFT #ARC`;

  const downloadVideo = () => {
    if (!videoUrl) return;
    const a      = document.createElement('a');
    a.href       = videoUrl;
    a.download   = `hexabots-run-${wallet.slice(0, 6)}.${ext}`;
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  const openX = () => {
    downloadVideo();
    window.open(
      `https://twitter.com/intent/tweet?text=${encodeURIComponent(tweetText)}`,
      '_blank',
      'noopener,noreferrer'
    );
  };

  // WebM duration fix — forces browser to scan the file so scrubber works
  const fixWebmDuration = (e) => {
    const v = e.currentTarget;
    if (v.duration === Infinity || isNaN(v.duration)) {
      v.currentTime = 1e101;
      const reset = () => {
        v.currentTime = 0;
        v.removeEventListener('timeupdate', reset);
      };
      v.addEventListener('timeupdate', reset);
    }
  };

  // Mobile browsers — especially in-app webviews — are much less
  // reliable about honoring `autoPlay` than desktop, even when muted +
  // playsInline are set. If the browser silently blocks it, fall back
  // to a tap-to-play overlay instead of leaving a frozen black frame.
  const attemptPlay = () => {
    const v = videoRef.current;
    if (!v) return;
    const p = v.play();
    if (p && typeof p.catch === 'function') {
      p.then(() => setNeedsTap(false)).catch(() => setNeedsTap(true));
    }
  };

  useEffect(() => {
    if (videoUrl) attemptPlay();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [videoUrl]);

  // Block Escape from closing the modal until the form is done
  useEffect(() => {
    if (canClose) return;
    const onKeyDown = (e) => {
      if (e.key === 'Escape') e.preventDefault();
    };
    window.addEventListener('keydown', onKeyDown, { capture: true });
    return () => window.removeEventListener('keydown', onKeyDown, { capture: true });
  }, [canClose]);

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4"
      style={{ background: 'rgba(3,5,10,0.88)', backdropFilter: 'blur(6px)' }}
      onClick={canClose ? onClose : undefined}
    >
      <div
        className="mech-panel mech-corners relative w-full max-w-xl"
        onClick={(e) => e.stopPropagation()}
        style={{ maxHeight: '90vh', overflowY: 'auto' }}
      >
        <div className="mech-panel-header">
          <span className="status-dot" />
          <GlowText text={step === 1 ? 'WALLET LOCK // TERMINAL' : 'SHARE // BROADCAST'} size={12} variant="cyan" />
        </div>

        <div className="p-6 md:p-8 space-y-5">

        {canClose && (
          <button
            onClick={onClose}
            aria-label="Close modal"
            className="btn-robotic btn-robotic-ghost absolute top-3 right-3 w-8 h-8 !px-0 !py-0"
            style={{ fontSize: 18 }}
          >
            ×
          </button>
        )}

        {/* ── Step 1: ETH Wallet Input ── */}
        {step === 1 && (
          <div className="space-y-5">
            <p className="font-mono-hud text-fluid-lg" style={{ color: 'var(--text)' }}>
              Drop your ETH wallet address. This locks your GTD spot in the
              HEXABOTS whitelist.
            </p>

            <p className="font-mono-hud text-fluid-sm text-dim">
              This window stays open until you submit your wallet below.
            </p>

            <input
              autoFocus
              type="text"
              placeholder="0x…"
              value={wallet}
              disabled={saving}
              onChange={(e) => setWallet(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && !saving && next()}
              className="hud-input"
            />

            <RoboticButton onClick={next} disabled={saving} variant="cyan" fullWidth>
              {saving ? 'SAVING…' : 'NEXT'}
            </RoboticButton>
          </div>
        )}

        {/* ── Step 2: Share on X ── */}
        {step === 2 && (
          <div className="space-y-5">
            <p className="font-mono-hud text-fluid-md" style={{ color: 'var(--text)' }}>
              Post your recorded run on X to become GTD eligible. Your wallet
              is already saved — it is deliberately left out of the post so it
              stays private.
            </p>

            {videoUrl ? (
              <div className="mech-panel relative w-full overflow-hidden" style={{ padding: 4, background: '#000' }}>
                <video
                  ref={videoRef}
                  src={videoUrl}
                  controls
                  autoPlay
                  muted
                  loop
                  playsInline
                  webkit-playsinline="true"
                  preload="auto"
                  disablePictureInPicture
                  onLoadedMetadata={fixWebmDuration}
                  onCanPlay={attemptPlay}
                  className="block w-full h-auto max-h-[50vh] object-contain"
                />
                {needsTap && (
                  <button
                    type="button"
                    onClick={attemptPlay}
                    aria-label="Play video"
                    className="absolute inset-0 flex items-center justify-center"
                    style={{ background: 'rgba(3,6,13,0.5)', cursor: 'pointer' }}
                  >
                    <span className="btn-robotic btn-robotic-cyan px-6 py-3 text-sm">TAP TO PLAY</span>
                  </button>
                )}
              </div>
            ) : (
              <div className="font-mono-hud text-dim text-center py-4">
                Recording unavailable in this browser.
              </div>
            )}

            {/* Pre-written tweet preview */}
            <div className="hud-chip w-full font-mono-hud text-sm whitespace-pre-wrap" style={{ display: 'block', padding: '14px 16px', color: 'var(--cyan)' }}>
              {tweetText}
            </div>

            <p className="font-mono-hud text-fluid-sm text-dim">
              X does not allow direct video upload from third-party sites.
              Your video will be downloaded automatically — attach it in the
              X composer that opens.
            </p>

            <div className="flex flex-col sm:flex-row gap-3">
              <RoboticButton onClick={downloadVideo} disabled={!videoUrl} variant="ghost" fullWidth>
                DOWNLOAD CLIP
              </RoboticButton>
              <RoboticButton onClick={openX} variant="violet" fullWidth>
                POST ON X
              </RoboticButton>
            </div>
          </div>
        )}
        </div>
      </div>
    </div>
  );
}
